import axios from 'axios';
import Cookies from 'js-cookie';

export const API = import.meta.env.VITE_API_URL;

const token = Cookies.get('authState')
  ? JSON.parse(Cookies.get('authState')!).token
  : '';

const AXIOS = axios.create({
  baseURL: API,
  headers: {
    Authorization: `Bearer ${token}`,
  },
});

// Lock for refreshing token
let refreshPromise: Promise<void> | null = null;

const isTokenExpiringSoon = (expirationDate: string) => {
  const timeBuffer = 2 * 60 * 1000; // 2 minutes
  return new Date(expirationDate).getTime() - timeBuffer < Date.now();
};

const performRefreshToken = async () => {
  const refreshTokenCookie = Cookies.get('authRefreshToken');
  if (!refreshTokenCookie) {
    throw new Error('No refresh token available');
  }

  let refreshTokenString = '';
  try {
    const parsed = JSON.parse(refreshTokenCookie);
    refreshTokenString = parsed.token;
  } catch (e) {
    throw new Error('Invalid refresh token format');
  }

  const response = await axios.post(`${API}/api/security/token/refresh`, {
    refreshToken: refreshTokenString,
  });

  const { user, token, expiredAt, refreshToken, refreshTokenExpiration } = response.data;

  const newAuthState = {
    user,
    token,
    tokenExpiration: expiredAt,
    avatarUrl: user.avatarUrl,
  };
  Cookies.set('authState', JSON.stringify(newAuthState));

  Cookies.set('authRefreshToken', JSON.stringify({
    token: refreshToken,
    tokenExpiration: refreshTokenExpiration,
  }));

  console.log('Token successfully refreshed')
};

AXIOS.interceptors.request.use(async (config) => {
  if (config.url?.includes('/api/security/token/refresh')) {
    return config;
  }

  const authStateCookie = Cookies.get('authState');

  if (!authStateCookie) {
    return config;
  }

  try {
    const parsedAuth = JSON.parse(authStateCookie);

    if (parsedAuth.tokenExpiration && isTokenExpiringSoon(parsedAuth.tokenExpiration)) {
      if (!refreshPromise) {
        console.log('Token expiring soon - starting refresh...');
        refreshPromise = performRefreshToken()
          .catch((err) => {
            console.error('Auto-refresh failed:', err);
            Cookies.remove('authState')
            Cookies.remove('authRefreshToken'); // todo setAuthState to null
            throw err;
          })
          .finally(() => {
            refreshPromise = null;
          });
      }

      await refreshPromise;
    }

    const freshCookie = Cookies.get('authState');
    if (freshCookie) {
      const { token } = JSON.parse(freshCookie);
      config.headers.Authorization = `Bearer ${token}`;
    }
  } catch (e) {
    console.error('Error in request interceptor', e);
  }

  return config;
});

export default AXIOS;
